import type { LayoutLine } from "./SplitMeasuredTokens.ts";
import type { ISplitOptions } from "../../models/text/ISplitOptions.ts";
import { SplitOptionsPredicates } from "./SplitOptionsPredicates.ts";

export type FitContext = {
  currentFontSize: number;
  contentWidth: number;
  /** Computed line-height in px at currentFontSize. 0 when unavailable (e.g. "normal"). */
  lineHeightPx: number;
};

/**
 * Pure math for `string-split="fit"`: figuring out what font size makes a
 * line of text fill a target width. No DOM access — every input is a
 * pre-measured number, every output is a plain number.
 */
export class FitFontSizeCalculator {
  /**
   * Target font size to fill `contentWidth`, given a line currently
   * `lineWidth` px wide at `currentFontSize`.
   *
   * Inter-word ` ` nodes are not wrapped in `-s-char` spans when chars
   * are split, so they stay at the original font size and don't scale. When
   * `browserWordWidthSum` is provided (char-level split active), that width
   * is excluded from both sides of the ratio so only the scalable glyphs are
   * sized against `contentWidth`:
   *
   *   targetFontSize = currentFontSize * (contentWidth - spacesWidth) / (lineWidth - spacesWidth)
   *
   * With `browserWordWidthSum` omitted (no char split), spacesWidth is 0 and
   * this reduces to the plain ratio `currentFontSize * (contentWidth / lineWidth)`.
   */
  static compute(
    currentFontSize: number,
    contentWidth: number,
    lineWidth: number,
    browserWordWidthSum?: number,
  ): number {
    const spacesWidth = browserWordWidthSum !== undefined ? lineWidth - browserWordWidthSum : 0;
    const scalableWidth = lineWidth - spacesWidth;
    if (scalableWidth <= 0) return currentFontSize * (contentWidth / lineWidth);
    return (currentFontSize * (contentWidth - spacesWidth)) / scalableWidth;
  }

  /**
   * Corrects a provisional fit font size against two real measurements
   * (`baselineWidth` at `currentFontSize`, `trialWidth` at
   * `provisionalFontSize`) by fitting a line through them and solving for
   * the font size that lands on `targetWidth`.
   *
   * `computeFitFontSize` assumes glyph width scales exactly linearly with
   * font size, which browsers don't guarantee (hinting, subpixel rounding).
   * This measures two real points and corrects for the actual slope instead
   * of trusting the assumption.
   *
   * Returns `null` when the inputs can't support a linear solve (a
   * measurement come back non-positive, or the two font sizes produced no
   * measurable width difference).
   */
  static solve(
    currentFontSize: number,
    provisionalFontSize: number,
    targetWidth: number,
    baselineWidth: number,
    trialWidth: number,
  ): number | null {
    if (!Number.isFinite(currentFontSize) || !Number.isFinite(provisionalFontSize)) return null;
    if (currentFontSize <= 0 || provisionalFontSize <= 0) return null;
    if (baselineWidth <= 0) return null;

    if (Math.abs(targetWidth - baselineWidth) < 0.01) {
      return currentFontSize;
    }

    if (trialWidth <= 0) {
      return currentFontSize * (targetWidth / baselineWidth);
    }

    const slope = (trialWidth - baselineWidth) / (provisionalFontSize - currentFontSize);
    if (!Number.isFinite(slope) || Math.abs(slope) < 0.0001) {
      return currentFontSize * (targetWidth / baselineWidth);
    }

    const corrected = currentFontSize + (targetWidth - baselineWidth) / slope;
    if (!Number.isFinite(corrected) || corrected <= 0) return null;
    return corrected;
  }

  /**
   * Applies `compute()` across a split's layout lines.
   *
   * With a line-level split (`line`, `wordLine`, `charLine` options set):
   * mutates `fitFontSize` on every line, so each line fills the container
   * width independently. Without one: finds the widest line and returns a
   * single `--fit-font-size` value for the whole container, since there's
   * only one span to size.
   *
   * `browserWordWidthSum` is only passed to `compute()` when a char-level
   * split is active (inter-word spaces are unscaled text nodes only in that
   * case — see `compute()`'s doc comment).
   */
  static applyToLines(
    layoutLines: LayoutLine[],
    options: ISplitOptions,
    fitContext: FitContext,
  ): Map<string, string> {
    const extraProps = new Map<string, string>();
    const { currentFontSize, contentWidth } = fitContext;

    const hasLineSplit = SplitOptionsPredicates.hasLineDrivenSplit(options);
    const hasCharSplit = SplitOptionsPredicates.hasCharDrivenSplit(options);

    if (hasLineSplit) {
      for (const line of layoutLines) {
        const lineWidth = line.fitWidth ?? line.rect.width;
        if (lineWidth > 0) {
          line.fitFontSize = FitFontSizeCalculator.compute(
            currentFontSize,
            contentWidth,
            lineWidth,
            hasCharSplit ? line.browserWordWidthSum : undefined,
          );
        }
      }
    } else {
      const widestLine = layoutLines.reduce((best, l) => {
        const w = l.fitWidth ?? l.rect.width;
        return w > (best.fitWidth ?? best.rect.width) ? l : best;
      }, layoutLines[0]);
      const widestLineWidth = widestLine.fitWidth ?? widestLine.rect.width;
      const targetFontSize = FitFontSizeCalculator.compute(
        currentFontSize,
        contentWidth,
        widestLineWidth,
        hasCharSplit ? widestLine.browserWordWidthSum : undefined,
      );
      extraProps.set("--fit-font-size", String(Math.floor(targetFontSize)));
    }

    return extraProps;
  }
}
